// src/pages/Services.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaCar, 
  FaShieldAlt,
  FaMotorcycle, 
  FaMobile, 
  FaMapMarkedAlt, 
  FaTruck, 
  FaWrench,
  FaSearchLocation,
  FaRocket,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaStar,
  FaArrowRight,
  FaPlay,
  FaWhatsapp
} from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const navigate = useNavigate();

  const handleScheduleClick = () => {
    navigate('/scheduling');
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = '5585999835383';
    const message = 'Olá! Gostaria de saber mais sobre os serviços da G3 Rastreamento.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const services = [
    {
      icon: <FaCar />,
      title: "Rastreamento Veicular",
      description: "Monitore seu veículo em tempo real com nosso sistema de rastreamento avançado. Controle de velocidade, localização precisa, histórico de rotas e bloqueio remoto.",
      features: ["Monitoramento 24/7", "Localização em tempo real", "Histórico de rotas", "Controle de velocidade", "Bloqueio remoto"],
      price: "R$ 45,00/mês",
      category: "rastreamento",
      popular: false
    },
    {
      icon: <FaMotorcycle />,
      title: "Plano Moto +",
      description: "Sua moto protegida, assistida e sempre pronta para rodar. Muito mais que um rastreador: rede completa de proteção, assistência 24h e benefícios exclusivos de oficina para você e sua moto.",
      features: [
        "Rastreamento 24h em tempo real",
        "Bloqueio via aplicativo e cerca eletrônica",
        "Assistência 24h (reboque, chaveiro, borracharia e panes)",
        "3 revisões e 3 lavagens ao ano",
        "1 bateria nova ao ano + AirTag e antifurto em comodato"
      ],
      price: "A partir de R$ 59,00/mês",
      category: "moto",
      popular: true
    },
    {
      icon: <FaShieldAlt />,
      title: "Rastreamento + Antifurto",
      description: "Proteção máxima com dispositivo antifurto integrado ao sistema de rastreamento. Maior segurança contra tentativas de furto.",
      features: ["Dispositivo antifurto", "Bloqueio de ignição", "Sensor de vibração", "Alerta de tentativa de furto", "Suporte 24/7"],
      price: "R$ 50,00 + Mensalidade",
      category: "seguranca",
      popular: false
    },
    {
      icon: <FaTruck />,
      title: "Assistência Veicular",
      description: "Serviços de assistência completa para seu veículo, incluindo reboque, auxílio em pane e atendimento de emergência 24 horas.",
      features: ["Reboque 24/7", "Auxílio em pane", "Borracheiro", "Chaveiro", "Cobertura nacional"],
      price: "R$ 57,00/mês",
      category: "assistencia",
      popular: false
    },
    {
      icon: <FaSearchLocation />,
      title: "Recuperação de Veículos",
      description: "Equipe especializada em recuperação de veículos com agilidade e eficiência. Atuamos em todo o território nacional.",
      features: ["Equipe especializada", "Recuperação rápida", "Cobertura nacional", "Suporte técnico", "Relatórios detalhados"],
      price: "Sob consulta",
      category: "recuperacao",
      popular: false
    },
    {
      icon: <FaMobile />,
      title: "Aplicativo Mobile",
      description: "Acesse todas as funcionalidades do sistema diretamente do seu celular. Interface intuitiva e funcionalidades avançadas.",
      features: ["Android e iOS", "Interface intuitiva", "Notificações push", "Mapas offline", "Sincronização em tempo real"],
      price: "Incluso",
      category: "tecnologia",
      popular: false
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Cerca Virtual",
      description: "Defina áreas de circulação para seu veículo e receba alertas em tempo real quando ele sair dos limites estabelecidos.",
      features: ["Definição de áreas", "Alertas em tempo real", "Histórico de violações", "Configuração personalizada", "Múltiplas cercas"],
      price: "Incluso",
      category: "tecnologia",
      popular: false
    },
  ];

  const benefits = [
    {
      icon: <FaRocket />,
      title: "Tecnologia de Ponta",
      description: "Utilizamos as mais modernas tecnologias do mercado para garantir precisão e confiabilidade"
    },
    {
      icon: <FaUsers />,
      title: "Atendimento Premium",
      description: "Suporte técnico especializado e dedicado, disponível 24 horas por dia"
    },
    {
      icon: <FaShieldAlt />,
      title: "Segurança Garantida",
      description: "Sistemas certificados e testados para máxima proteção do seu veículo"
    },
    {
      icon: <FaClock />,
      title: "Cobertura Nacional",
      description: "Monitoramento em todo território brasileiro com rede de parceiros"
    }
  ];

  const stats = [
    { number: "10+", label: "Anos de Experiência" },
    { number: "1000+", label: "Clientes Satisfeitos" },
    { number: "99.9%", label: "Taxa de Sucesso" },
    { number: "24/7", label: "Suporte Disponível" }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-background">
          <div className="hero-pattern"></div>
        </div>
        <div className="services-hero-content">
          <div className="hero-badge">
            <FaStar className="badge-icon" />
            <span>Serviços Premium</span>
          </div>
          <h1>Nossos Serviços</h1>
          <p>Soluções completas em rastreamento e monitoramento veicular com tecnologia de ponta e atendimento premium</p>
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <div className="stat-item" key={index}>
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="services-grid-section">
        <div className="section-header">
          <h2>Nossas Soluções</h2>
          <p>Conheça todos os nossos serviços e escolha o que melhor atende suas necessidades</p>
        </div>
        <div className="services-container">
          {services.map((service, index) => (
            <div className={`service-card ${service.popular ? 'popular' : ''}`} key={index}>
              {service.popular && (
                <div className="popular-badge">
                  <FaStar className="popular-icon" />
                  <span>Mais Popular</span>
                </div>
              )}
              <div className="service-header">
                <div className="service-icon-wrapper">
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <div className="service-price">{service.price}</div>
              </div>
              <p className="service-description">{service.description}</p>
              <div className="service-features">
                {service.features.map((feature, featureIndex) => (
                  <div className="feature-item" key={featureIndex}>
                    <FaCheckCircle className="feature-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <div className="service-actions">
                <button className="service-button primary" onClick={handleWhatsAppClick}>
                  <FaWhatsapp className="button-icon" />
                  Saiba Mais
                </button>
                <button className="service-button secondary" onClick={handleScheduleClick}>
                  <FaArrowRight className="button-icon" />
                  Agendar
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-background">
          <div className="benefits-pattern"></div>
        </div>
        <div className="benefits-content">
          <div className="section-header light">
            <h2>Por que escolher nossos serviços?</h2>
            <p>Descubra as vantagens que fazem da G3 Rastreamento a melhor escolha para você</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div className="benefit-card" key={index}>
                <div className="benefit-icon-wrapper">
                  {benefit.icon}
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-background">
          <div className="cta-pattern"></div>
        </div>
        <div className="cta-content">
          <div className="section-header light">
            <h2>Pronto para começar?</h2>
            <p>Agende agora mesmo a instalação do seu rastreador e garanta a segurança do seu veículo</p>
          </div>
          <div className="cta-actions">
            <button 
              className="cta-button primary"
              onClick={handleScheduleClick}
            >
              <FaPlay className="button-icon" />
              Agende sua Instalação
            </button>
            <button className="cta-button secondary" onClick={handleWhatsAppClick}>
              <FaWhatsapp className="button-icon" />
              Falar com Consultor
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;